"use client";
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "visuais";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import { RateioTable } from "./RateioTable";
import { useRateio } from "./useRateio";
import { usePrenotaStore, type RateioOC } from "@PN/inclusao";

interface RateioOCAgrupado {
  codPedido: string;
  itemPedido: string;
  codProduto: string;
  descricao: string;
  rateios: RateioOC[];
}

interface ExpandedRateioContentProps {
  isExpanded: boolean;
  onClose: () => void;
}

export function ExpandedRateioContent({
  isExpanded,
  onClose,
}: ExpandedRateioContentProps) {
  const { calculations, temRegrasEspeciais, aplicarRateioFixo, temRateioFixo } = useRateio();
  const [showRateiosOC, setShowRateiosOC] = useState(false);

  const produtosProtheus = usePrenotaStore((state) => state.aux.produtoProtheus);

  // Agrupa rateios do pedido de compra por produto
  const rateiosOCAgrupados = useMemo<RateioOCAgrupado[]>(() => {
    if (!produtosProtheus || produtosProtheus.length === 0) return [];

    return produtosProtheus
      .filter((p) => p.rateiosOC && p.rateiosOC.length > 0)
      .map((p) => ({
        codPedido: p.pedidoCompra || "",
        itemPedido: p.itemPedido || "",
        codProduto: p.codProduto || "",
        descricao: p.descricao || "",
        rateios: p.rateiosOC || [],
      }));
  }, [produtosProtheus]);

  const totalRateiosOC = useMemo(
    () => rateiosOCAgrupados.reduce((sum, g) => sum + g.rateios.length, 0),
    [rateiosOCAgrupados]
  );

  return (
    <AnimatePresence>
      {isExpanded && (
        <>
          <div className="fixed inset-0 backdrop-blur-sm z-10" />
          <motion.div
            layoutId="rateio-card"
            className="fixed inset-0 grid place-items-center z-[20] p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
          >
            <motion.div
              className="w-full max-w-[95vw] border-2 border-border h-full max-h-[95vh] flex flex-col bg-background rounded-lg shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header com botão de voltar */}
              <div className="flex items-center justify-between p-4 border-b border-border bg-background/95 backdrop-blur-sm flex-shrink-0">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold">Configurar Rateio</h2>
                  {temRegrasEspeciais && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Regras Especiais
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Botão para mostrar rateios do pedido de compra */}
                  {totalRateiosOC > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRateiosOC((v) => !v)}
                      className="gap-1.5 text-xs"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Rateios do Pedido ({totalRateiosOC})
                      {showRateiosOC ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                  {temRateioFixo && (
                    <div className="relative inline-block">
                      <Button
                        onClick={aplicarRateioFixo}
                        size="lg"
                        className="relative text-md bg-blue-500 text-white hover:bg-blue-600 overflow-hidden"
                      >
                        <span className="relative z-10">Aplicar Rateio Fixo</span>
                        <span className="absolute inset-0 rounded-md border-2 border-transparent [background:linear-gradient(var(--tw-gradient-from),var(--tw-gradient-to))_border-box] [--tw-gradient-from:#60a5fa] [--tw-gradient-to:transparent] animate-[spin_5s_linear_infinite]" />
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="hover:bg-muted/80"
                  >
                    <svg
                      className="size-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                </div>
              </div>

              {/* Painel de rateios do pedido de compra (colapsável) */}
              <AnimatePresence>
                {showRateiosOC && totalRateiosOC > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    className="overflow-hidden border-b border-border"
                  >
                    <div className="p-4 bg-blue-50/50 dark:bg-blue-950/20 max-h-[30vh] overflow-auto">
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Rateios originais do Pedido de Compra
                        </span>
                        <span className="text-xs text-blue-600/70 dark:text-blue-400/70">
                          (somente leitura)
                        </span>
                      </div>

                      <div className="space-y-3">
                        {rateiosOCAgrupados.map((grupo, idx) => (
                          <div
                            key={`${grupo.codPedido}-${grupo.itemPedido}-${idx}`}
                            className="rounded-md border border-blue-200 dark:border-blue-800 bg-white/60 dark:bg-background/60 overflow-hidden"
                          >
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100/50 dark:bg-blue-900/30 text-xs">
                              <span className="font-medium">
                                Pedido {grupo.codPedido}
                              </span>
                              <span className="text-muted-foreground">
                                Item {grupo.itemPedido}
                              </span>
                              <span className="text-muted-foreground">-</span>
                              <span className="truncate text-muted-foreground" title={grupo.descricao}>
                                {grupo.codProduto} - {grupo.descricao}
                              </span>
                            </div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-blue-100 dark:border-blue-900 text-muted-foreground">
                                  <th className="px-3 py-1 text-left font-medium">Filial</th>
                                  <th className="px-3 py-1 text-left font-medium">Centro de Custo</th>
                                  <th className="px-3 py-1 text-right font-medium">%</th>
                                  <th className="px-3 py-1 text-right font-medium">Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {grupo.rateios.map((r, rIdx) => (
                                  <tr
                                    key={`${r.codCentroCusto}-${r.itemRateio}-${rIdx}`}
                                    className="border-b last:border-b-0 border-blue-50 dark:border-blue-900/50"
                                  >
                                    <td className="px-3 py-1 font-mono">{r.filPedido}</td>
                                    <td className="px-3 py-1 truncate max-w-[280px]" title={`${r.codCentroCusto} - ${r.descCentroCusto}`}>
                                      {r.codCentroCusto} - {r.descCentroCusto}
                                    </td>
                                    <td className="px-3 py-1 text-right font-mono">
                                      {r.percentual.toFixed(2).replace(".", ",")}%
                                    </td>
                                    <td className="px-3 py-1 text-right font-mono">
                                      R$ {r.valorRateio.toFixed(2).replace(".", ",")}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Conteúdo da tabela - tela cheia */}
              <div className="flex-1 p-6 overflow-auto">
                <RateioTable />
              </div>

              {/* Footer com resumo financeiro */}
              <div className="flex-shrink-0 border-t border-border p-4 bg-muted/30">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-muted-foreground">Total da NF</div>
                    <div className="font-semibold text-base">
                      R$ {calculations.totalGeral.toFixed(2).replace(".", ",")}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground">Valor Rateado</div>
                    <div className="font-semibold text-base text-blue-600">
                      R${" "}
                      {calculations.totalRateado.toFixed(2).replace(".", ",")}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground">Percentual</div>
                    <div className="font-semibold text-base text-blue-600">
                      {calculations.percentualRateado.toFixed(2)}%
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground">Valor Restante</div>
                    <div
                      className={`font-semibold text-base ${
                        calculations.restante < 0.01
                          ? "text-green-600"
                          : calculations.restante < 0
                          ? "text-red-600"
                          : "text-orange-600"
                      }`}
                    >
                      R$ {calculations.restante.toFixed(2).replace(".", ",")}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
